import { forwardRef } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Minus, Plus } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';

export interface DatePickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  label?: string;
  error?: string;
  helperText?: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  includeTime?: boolean;
}

const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  ({ className, label, error, helperText, value, onChange, includeTime = false, id, disabled, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      if (inputValue) {
        // Parse as local time, not UTC
        // "2026-03-01" should be March 1st local, not Feb 28th in US timezones
        if (includeTime) {
          onChange?.(new Date(inputValue));
        } else {
          // For date-only input, append time to force local parsing
          onChange?.(new Date(inputValue + 'T00:00:00'));
        }
      } else {
        onChange?.(null);
      }
    };

    const handleDecrement = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (disabled) return;
      if (value) {
        onChange?.(subDays(value, 1));
      } else {
        // If no date selected, start with today minus 1
        onChange?.(subDays(new Date(), 1));
      }
    };

    const handleIncrement = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (disabled) return;
      if (value) {
        onChange?.(addDays(value, 1));
      } else {
        // If no date selected, start with today plus 1
        onChange?.(addDays(new Date(), 1));
      }
    };

    const formatValue = () => {
      if (!value) return '';
      if (includeTime) {
        return format(value, "yyyy-MM-dd'T'HH:mm");
      }
      return format(value, 'yyyy-MM-dd');
    };

    const buttonBaseClass = clsx(
      'flex items-center justify-center px-2 py-2 transition-colors',
      disabled
        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300 cursor-pointer'
    );

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div
          className={clsx(
            'flex items-stretch rounded-lg border overflow-hidden',
            error
              ? 'border-red-500 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20'
              : 'border-gray-300 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20'
          )}
        >
          {/* Decrement button */}
          <button
            type="button"
            onClick={handleDecrement}
            onMouseDown={(e) => e.preventDefault()}
            disabled={disabled}
            className={clsx(buttonBaseClass, 'border-r border-gray-300')}
            tabIndex={-1}
            aria-label="Previous day"
          >
            <Minus className="h-4 w-4" />
          </button>

          {/* Date input */}
          <input
            ref={ref}
            id={inputId}
            type={includeTime ? 'datetime-local' : 'date'}
            value={formatValue()}
            onChange={handleChange}
            disabled={disabled}
            onClick={(e) => {
              // Open calendar picker when clicking anywhere in the field
              const input = e.target as HTMLInputElement;
              input.showPicker?.();
            }}
            className={twMerge(
              clsx(
                'flex-1 px-0 py-2 text-gray-900 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer text-center min-w-0 relative',
                // Hide native calendar icon but keep it clickable within input bounds
                '[&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer',
                className
              )
            )}
            {...props}
          />

          {/* Increment button */}
          <button
            type="button"
            onClick={handleIncrement}
            onMouseDown={(e) => e.preventDefault()}
            disabled={disabled}
            className={clsx(buttonBaseClass, 'border-l border-gray-300')}
            tabIndex={-1}
            aria-label="Next day"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        {helperText && !error && <p className="mt-1 text-sm text-gray-500">{helperText}</p>}
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export { DatePicker };
